import { ChevronDown, FlaskConical } from 'lucide-react';
import { Pill } from '@shared/ui';
import { useAccountConnection } from '../hooks/useAccountConnection';
import { useConnectDialogStore } from '../state/connectDialogStore';

/**
 * The Figma top-bar account/connection control (2026-07 redesign, re-drawn 2026-09-02) — ONE button
 * replacing the old `AccountDropdown` + both provider state badges: status dot · LIVE/DEMO chip ·
 * bold broker prefix + account name · chevron. Clicking toggles the Accounts panel, which is where
 * switching, linking and adding accounts happen.
 *
 * The dot is the AGGREGATE alarm surface (decision 2026-07-18, KEPT by the user on 2026-09-02 when
 * the Figma showed none): it shows the selected account's state normally, but flips to loss/warning
 * when ANY provider session is in error / taken over by a concurrent session — so a Rithmic-selected
 * user still notices their CQG session dropping. Details live in the panel; the tooltip names the
 * provider and the connection state.
 *
 * 2026-09-02 Figma ("for the top level panel, when we select live or demo account, I would like to
 * present it like here"): the chip reads the account's GATEWAY ENVIRONMENT — a grey **LIVE** pill or
 * a light-blue **DEMO** pill with a flask glyph — instead of the connection status words
 * (Connected/Connecting/Error/Offline), which now live only in the tooltip and the dot. The label's
 * broker prefix renders bold ("**CQG:** Simcgxl Pscqgxl"). Accounts with no environment (Rithmic,
 * imported) show no pill at all rather than a made-up one.
 */
export function AccountConnectionControl() {
  const { selected, alert } = useAccountConnection();
  const togglePanel = useConnectDialogStore(s => s.togglePanel);

  const stateDot: Record<typeof selected.display, string> = {
    live: 'var(--gain)',
    connecting: 'var(--accent)',
    error: 'var(--loss)',
    offline: 'var(--text-subtle)',
  };
  const dotColor = alert
    ? (alert.kind === 'concurrent' ? 'var(--warning)' : 'var(--loss)')
    : stateDot[selected.display];

  // The connection STATUS words moved out of the visible control and into the tooltip (2026-09-02):
  // the chip now carries the environment, and the dot carries the state.
  const stateLabel: Record<typeof selected.display, string> = {
    live: 'Connected',
    connecting: 'Connecting',
    error: 'Error',
    offline: 'Offline',
  };

  const title = alert
    ? `${alert.provider.toUpperCase()} session ${alert.kind === 'concurrent' ? 'is in use elsewhere' : 'error'}: open the accounts panel`
    : selected.mismatch
      ? "The connected login doesn't own this account. Link it with its own login"
      : `Account connection: ${stateLabel[selected.display]}`;

  return (
    <button
      type="button"
      onClick={togglePanel}
      title={title}
      aria-label="Accounts"
      className="flex h-9 items-center gap-3 rounded-lg px-3 transition-opacity hover:opacity-90"
      style={{ background: 'var(--canvas)' }}
    >
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: dotColor }} aria-hidden />
      {/* LIVE = the grey chip; DEMO = light blue (--accent-soft) with the flask glyph, per the Figma.
          No pill when the account has no gateway environment. A live-gateway account that is offline
          reads OFFLINE in the same grey chip (owner, 2026-10-20: "very confusing to read this content
          and be however offline. Can we replace at least the 'live' by 'offline'"). */}
      {selected.environment === 'live' && (
        <Pill bg="var(--canvas-divider-strong)" fg="var(--chip-text)">
          {selected.display === 'offline' ? 'Offline' : 'Live'}
        </Pill>
      )}
      {selected.environment === 'demo' && (
        <Pill bg="var(--accent-soft)" fg="var(--chip-text)" icon={FlaskConical}>Demo</Pill>
      )}
      <span className="max-w-[12rem] truncate text-[13px] leading-5 text-text">
        {selected.labelPrefix && <span className="font-semibold">{selected.labelPrefix}: </span>}
        {selected.labelName}
      </span>
      <ChevronDown className="h-3 w-3 shrink-0 text-text" aria-hidden />
    </button>
  );
}
